/**
 * Чат-шаблоны. Каждый — свой скелет (build) + свой css. Косметика через --inbx-*.
 *  • support  — тёмный хедер, кнопка-звонок, чипы быстрых ответов, тулбар (как Jivo/Tidio).
 *  • minimal  — светлый лаконичный, без тулбара/чипов (как Webim/Crisp).
 *  • spacious — крупный градиентный hero-хедер, аватары, pill-композер (как Carrot quest/Intercom).
 */
import type { ChatContent, ProductCard, RuntimeWidgetConfig } from '@inbx/shared/schemas';

import { clear, el } from '../dom';

import type { BuildCtx, BuildInstance, WidgetTemplate } from './types';

type ChatC = ChatContent & { avatar_url?: string; status_text?: string; quick_replies?: string[] };

function avatar(url?: string): HTMLElement {
  const a = el('div', 'w-avatar');
  if (url) {
    const img = el('img');
    img.src = url;
    a.appendChild(img);
  }
  return a;
}

function bubble(text: string, dir: 'in' | 'out', avatarUrl?: string): HTMLElement {
  const row = el('div', `w-row w-row--${dir}`);
  if (dir === 'in' && avatarUrl !== undefined) row.appendChild(avatar(avatarUrl));
  row.appendChild(el('div', `w-bubble ${dir}`, text));
  return row;
}

/**
 * Товары, которые оператор предложил в переписке. Текстом (textContent) и
 * только http(s)-ссылками: данные пришли из чужой системы и рисуются на
 * странице покупателя.
 */
function productsRow(products: ProductCard[], avatarUrl?: string): HTMLElement {
  const row = el('div', 'w-row w-row--in');
  if (avatarUrl !== undefined) row.appendChild(avatar(avatarUrl));
  const list = el('div', 'w-products');
  for (const p of products) {
    const card = el('div', 'w-product');
    if (p.image_url && /^https?:\/\//i.test(p.image_url)) {
      const img = el('img', 'w-product__img');
      img.src = p.image_url;
      img.alt = p.title;
      img.loading = 'lazy';
      card.appendChild(img);
    }
    const body = el('div', 'w-product__body');
    body.appendChild(el('div', 'w-product__title', p.title));
    if (p.price) {
      const price = el('div', 'w-product__price', money(p.price));
      if (p.compare_at_price) price.appendChild(el('span', 'w-product__old', money(p.compare_at_price)));
      body.appendChild(price);
    }
    if (p.in_stock === false) body.appendChild(el('div', 'w-product__stock', 'Нет в наличии'));
    if (p.url && /^https?:\/\//i.test(p.url)) {
      const open = el('a', 'w-product__open', 'Открыть');
      open.href = p.url;
      open.target = '_blank';
      open.rel = 'noopener noreferrer';
      body.appendChild(open);
    }
    card.appendChild(body);
    list.appendChild(card);
  }
  row.appendChild(list);
  return row;
}

function money(m: { amount: string; currency: string }): string {
  const n = Number(m.amount);
  const amount = Number.isFinite(n) ? n.toLocaleString('ru-RU', { maximumFractionDigits: 2 }) : m.amount;
  return m.currency === 'RUB' ? `${amount} ₽` : `${amount} ${m.currency}`;
}

function composer(c: ChatC, offline: boolean, onSend: (t: string) => void): { form: HTMLFormElement } {
  const form = el('form', 'w-composer');
  const input = el('input');
  input.type = 'text';
  input.placeholder = offline ? 'ваш email' : c.placeholder ?? 'Введите сообщение';
  const send = el('button', 'w-send');
  send.type = 'submit';
  form.append(input, send);
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const t = input.value.trim();
    if (!t) return;
    onSend(t);
    input.value = '';
  });
  return { form };
}

/** Общий рендер ленты + возврат appendMessage. */
function messagesArea(c: ChatC, ctx: BuildCtx, withAvatar: boolean): { box: HTMLElement; append: BuildInstance['appendMessage'] } {
  const box = el('div', 'w-messages');
  const offline = ctx.state === 'offline';
  const av = withAvatar ? (c.avatar_url ?? '') : undefined;
  if (offline) {
    box.appendChild(bubble(c.offline_message ?? 'Сейчас мы офлайн. Оставьте сообщение.', 'in', av));
  } else {
    if (c.greeting) box.appendChild(bubble(c.greeting, 'in', av));
    if (ctx.demo) {
      box.appendChild(bubble('Здравствуйте!', 'out'));
      box.appendChild(bubble('Конечно, сейчас подскажу 🙂', 'in', av));
    }
  }
  const append: BuildInstance['appendMessage'] = (text, dir, avatarUrl, products) => {
    // Приоритет: аватар отправителя (оператор/бот) → дефолтный из контента.
    const sender = dir === 'in' ? (avatarUrl || av) : undefined;
    // Карточки заменяют текст: текст — их замена для каналов без карточек.
    box.appendChild(products?.length ? productsRow(products, sender) : bubble(text, dir, sender));
    box.scrollTop = box.scrollHeight;
  };
  return { box, append };
}

function send(ctx: BuildCtx) {
  return (t: string): void => {
    void ctx.handlers.onChatMessage?.(t);
  };
}

// ─────────────────────────── support ───────────────────────────
function buildSupport(ctx: BuildCtx): BuildInstance {
  const c = (ctx.config.content ?? {}) as ChatC;
  clear(ctx.root);
  const win = el('div', 'w-window');

  const header = el('div', 'w-header');
  const ht = el('div', 'w-head-text');
  ht.append(el('div', 'w-title', c.title ?? 'Чат с поддержкой'),
    el('div', 'w-status', ctx.state === 'offline' ? 'Не в сети' : c.status_text ?? 'Операторы онлайн'));
  const phone = el('button', 'w-head-action');
  phone.type = 'button';
  header.append(ht, phone);

  const { box, append } = messagesArea(c, ctx, false);

  const onSend = (t: string): void => { append!(t, 'out'); send(ctx)(t); };
  const { form } = composer(c, ctx.state === 'offline', onSend);

  win.append(header, box);
  const quick = c.quick_replies ?? (ctx.demo ? ['Уточнить по настройке', 'Вопрос по возможностям'] : []);
  if (quick.length) {
    const q = el('div', 'w-quick');
    for (const t of quick) { const b = el('button', 'w-quick-btn', t); b.type = 'button'; q.appendChild(b); }
    win.appendChild(q);
  }
  win.appendChild(form);
  const tools = el('div', 'w-tools');
  tools.append(el('span', 'w-tool w-tool-menu'), el('span', 'w-tool w-tool-attach'), el('span', 'w-tool w-tool-emoji'));
  win.appendChild(tools);
  ctx.root.appendChild(win);
  return { appendMessage: append };
}

// ─────────────────────────── minimal ───────────────────────────
function buildMinimal(ctx: BuildCtx): BuildInstance {
  const c = (ctx.config.content ?? {}) as ChatC;
  clear(ctx.root);
  const win = el('div', 'w-window');
  const header = el('div', 'w-header');
  header.append(el('span', 'w-title', c.title ?? 'Поддержка'));
  const { box, append } = messagesArea(c, ctx, false);
  const onSend = (t: string): void => { append!(t, 'out'); send(ctx)(t); };
  const { form } = composer(c, ctx.state === 'offline', onSend);
  win.append(header, box, form);
  ctx.root.appendChild(win);
  return { appendMessage: append };
}

// ─────────────────────────── spacious ──────────────────────────
function buildSpacious(ctx: BuildCtx): BuildInstance {
  const c = (ctx.config.content ?? {}) as ChatC;
  clear(ctx.root);
  const win = el('div', 'w-window');
  const hero = el('div', 'w-hero');
  hero.append(el('div', 'w-title', c.title ?? 'Чем помочь?'),
    el('div', 'w-status', c.status_text ?? 'Обычно отвечаем за пару минут'));
  const { box, append } = messagesArea(c, ctx, true);
  const onSend = (t: string): void => { append!(t, 'out'); send(ctx)(t); };
  const { form } = composer(c, ctx.state === 'offline', onSend);
  win.append(hero, box, form);
  ctx.root.appendChild(win);
  return { appendMessage: append };
}

// ─────────────────────────── CSS ───────────────────────────────
export const CHAT_TEMPLATES: WidgetTemplate[] = [
  { id: 'chat-support', type: 'chat', build: buildSupport },
  { id: 'chat-minimal', type: 'chat', build: buildMinimal },
  { id: 'chat-spacious', type: 'chat', build: buildSpacious },
];
